import { useCallback, useEffect, useRef, useState } from 'react';
import { bomClient } from '../client/bomClient';
import type { MinuteRange } from '../types/bom.types';

/**
 * Live supplier-research progress for dashboard rows. Only the counts the
 * buyer's own job owns are kept: research concurrency is per user, so the
 * server's `aheadFromOthers` is dropped here and never reaches a component.
 */
export interface RowResearch {
    done: number;
    running: number;
    waiting: number;
    total: number;
    etaMinutes: MinuteRange | null;
}

export const RESEARCH_POLL_MS = 45_000;

interface StatusResearch {
    done?: number;
    researching?: number;
    waiting?: number;
    total?: number;
    etaMinutes?: MinuteRange | null;
}

const toRowResearch = (r: StatusResearch): RowResearch => ({
    done: r.done ?? 0,
    running: r.researching ?? 0,
    waiting: r.waiting ?? 0,
    total: r.total ?? 0,
    etaMinutes: r.etaMinutes ?? null,
});

const isRunning = (r: RowResearch) => r.running + r.waiting > 0;

const isVisible = () => typeof document === 'undefined' || document.visibilityState === 'visible';

/**
 * `candidates` are rows that could be researching (in discovery), each with
 * the BOM id the list already carries — no per-row RFQ fetch. Each is probed
 * once; after that only rows still running are polled, every
 * RESEARCH_POLL_MS and only while the tab is visible. A finished row drops
 * out of the map, so its chip dissolves back to the normal stage tag.
 */
export interface ResearchCandidate {
    id: string;
    bomId: string | null;
}

export function useResearchProgress(candidates: ResearchCandidate[]): Record<string, RowResearch> {
    const [running, setRunning] = useState<Record<string, RowResearch>>({});
    const bomIds = useRef(new Map<string, string>());
    const probed = useRef(new Set<string>());
    const runningRef = useRef(running);
    runningRef.current = running;

    const refresh = useCallback(async (rfqId: string) => {
        try {
            const bomId = bomIds.current.get(rfqId);
            if (!bomId) return;
            const status = (await bomClient.getBOMStatus(bomId)) as { research?: StatusResearch };
            const next = status.research ? toRowResearch(status.research) : null;
            setRunning((prev) => {
                const rest = { ...prev };
                delete rest[rfqId];
                return next && isRunning(next) ? { ...rest, [rfqId]: next } : rest;
            });
        } catch {
            // A failed poll keeps the last known counts; the next tick retries.
        }
    }, []);

    candidates.forEach((c) => { if (c.bomId) bomIds.current.set(c.id, c.bomId); });
    const key = candidates.filter((c) => c.bomId).map((c) => c.id).sort().join('|');
    useEffect(() => {
        const fresh = key ? key.split('|').filter((id) => !probed.current.has(id)) : [];
        fresh.forEach((id) => {
            probed.current.add(id);
            void refresh(id);
        });
    }, [key, refresh]);

    useEffect(() => {
        const pollAll = () => {
            if (!isVisible()) return;
            Object.keys(runningRef.current).forEach((id) => void refresh(id));
        };
        const timer = setInterval(pollAll, RESEARCH_POLL_MS);
        const onVisible = () => { if (isVisible()) pollAll(); };
        document.addEventListener('visibilitychange', onVisible);
        return () => {
            clearInterval(timer);
            document.removeEventListener('visibilitychange', onVisible);
        };
    }, [refresh]);

    return running;
}
